import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Mail, CheckCircle } from "lucide-react";

const CATEGORIES = [
  ["general", "General question"],
  ["privacy", "Privacy request"],
  ["safety", "Safety / report"],
  ["legal", "Legal"],
  ["bug", "Bug / feedback"],
  ["other", "Other"],
];

export default function Contact() {
  const { user } = useAuth();
  const [name, setName] = useState(user?.display_name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [category, setCategory] = useState("general");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (!name.trim() || !email.includes("@") || message.trim().length < 5) {
      return toast.error("Please fill in your name, email, and a short message.");
    }
    setBusy(true);
    try {
      await api.post("/contact", { name, email, category, message });
      setSent(true);
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="max-w-lg mx-auto px-4 py-20 text-center" data-testid="contact-sent">
        <CheckCircle className="w-12 h-12 mx-auto text-called" strokeWidth={2.5} />
        <h1 className="font-display text-3xl font-bold mt-3">Message sent</h1>
        <p className="text-muted-foreground mt-1">Thanks — we'll get back to you as soon as we can.</p>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-10">
      <h1 className="font-display text-4xl font-bold tracking-tight flex items-center gap-2">
        <Mail className="w-7 h-7" /> Contact us
      </h1>
      <p className="text-muted-foreground mt-2 mb-6">
        Questions, privacy requests, or something to report? Send us a message.
      </p>
      <div className="card-brutal p-5 bg-white space-y-4">
        <div>
          <label className="text-xs font-bold uppercase">Your name</label>
          <Input data-testid="contact-name" value={name} onChange={(e) => setName(e.target.value)} className="border-2 border-ink mt-1 h-11" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase">Email</label>
          <Input data-testid="contact-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="border-2 border-ink mt-1 h-11" placeholder="you@example.com" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase">Topic</label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="contact-category" className="border-2 border-ink mt-1 h-11"><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIES.map(([id, l]) => <SelectItem key={id} value={id}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-bold uppercase">Message</label>
          <Textarea data-testid="contact-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={5} className="border-2 border-ink mt-1 resize-none" placeholder="How can we help?" />
        </div>
        <button data-testid="contact-submit" disabled={busy} onClick={submit}
          className="w-full h-12 border-2 border-ink bg-brand text-white font-bold btn-press shadow-brutal-sm disabled:opacity-50">
          {busy ? "Sending…" : "Send message"}
        </button>
      </div>
    </div>
  );
}
